/**
 * CheckpointSelector (Feature 2)
 *
 * Reusable grid of selectable training checkpoints. Picking a card persists
 * it as the LoRA's active checkpoint (`api.set_default_checkpoint`) — the one
 * every future inference resolves. Shared by the wizard's TrainingSlide (shown
 * the moment a run completes during the session) and LockSlide (so a
 * previously-trained LoRA can still re-pick its default).
 */

import { Badge } from "@/components/ui/badge";
import { CheckpointSampleThumbnail } from "@/components/loras/CheckpointSampleThumbnail";
import { cn } from "@/lib/utils";
import type { Checkpoint } from "@/types/loras";

const USABLE_CHECKPOINT: ReadonlySet<string> = new Set([
  "available",
  "evaluating",
  "evaluated",
  "ready",
]);

interface CheckpointSelectorProps {
  checkpoints: Checkpoint[];
  /** The LoRA's current default checkpoint id (the selected card). */
  defaultCheckpointId?: string | null;
  /** Persist the chosen checkpoint. */
  onSelect: (checkpointId: string) => void;
  /** set-default mutation in flight (disables the grid). */
  settingDefault?: boolean;
}

export function CheckpointSelector({
  checkpoints,
  defaultCheckpointId,
  onSelect,
  settingDefault,
}: CheckpointSelectorProps) {
  if (checkpoints.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2" data-testid="checkpoint-selector">
      {[...checkpoints]
        .sort((a, b) => b.stepNumber - a.stepNumber)
        .map((cp) => {
          const selected = defaultCheckpointId === cp.id;
          const usable = USABLE_CHECKPOINT.has(cp.statusSlug);
          return (
            <button
              key={cp.id}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={!usable || Boolean(settingDefault)}
              onClick={() => usable && onSelect(cp.id)}
              className={cn(
                "flex w-24 shrink-0 flex-col items-stretch gap-1 rounded-[8px] border bg-card p-2 text-left transition-colors",
                selected
                  ? "ring-2 ring-primary border-primary"
                  : usable && "hover:bg-muted/60",
                !usable && "cursor-not-allowed opacity-50",
              )}
            >
              <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-[6px] bg-muted text-[11px] font-medium text-muted-foreground">
                {cp.sampleInferenceAssetIds?.[0] ? (
                  <CheckpointSampleThumbnail
                    assetId={cp.sampleInferenceAssetIds[0]}
                    alt={`Step ${cp.stepNumber} sample`}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : (
                  <span>step {cp.stepNumber}</span>
                )}
              </div>
              <div className="flex items-center justify-between gap-1">
                <span className="truncate text-[11px] font-medium text-foreground">
                  Step {cp.stepNumber}
                </span>
                {selected && (
                  <Badge variant="outline" className="shrink-0 text-[9px]">
                    Selected
                  </Badge>
                )}
              </div>
            </button>
          );
        })}
    </div>
  );
}
